import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import config from "../config.js";
import { User, USER_TYPES, UserTypeValue } from "../types/userType";

// Add MessageType and ChatUser
export interface MessageType {
  _id?: string;
  message: string;
  sentTime: string;
  sender: string;
  direction: "incoming" | "outgoing";
  opened?: boolean;
  imagePath?: string; // Optional path to an image if the message is an image
}

export interface ChatUser extends User {
  messages: MessageType[];
}

interface UserContextType {
  error: string | null;
  setError: (error: string | null) => void;
  token: string | null;
  userType: string | UserTypeValue[] | null;
  setUserType: (userType: string | UserTypeValue[] | null) => void;
  login: (
    identifier: string,
    password: string
  ) => Promise<{ success: boolean; userType: string | null }>;
  logout: () => void;
  isAuthenticated: boolean;
  users: User[];
  setUsers: (users: User[]) => void;
  getUsers: (
    page?: number,
    reset?: boolean,
    phone?: string,
    userTypeFilter?: string
  ) => Promise<void>;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  hasMore: boolean;
  loadingMore: boolean;
  setLoadingMore: (loading: boolean) => void;
  reloadUsers: boolean;
  setReloadUsers: (reload: boolean) => void;
  usersWithActiveChat: ChatUser[];
  setUsersWithActiveChat: React.Dispatch<React.SetStateAction<ChatUser[]>>;
  usersWithChatHistory: ChatUser[];
  setUsersWithChatHistory: React.Dispatch<React.SetStateAction<ChatUser[]>>;
  isActiveChat: boolean;
  setIsActiveChat: React.Dispatch<React.SetStateAction<boolean>>;
  getUsersWithActiveChat: () => Promise<ChatUser[]>;
  getUsersWithChatHistory: () => Promise<ChatUser[]>;
  deleteMessage: (userId: string, messageId: string) => Promise<boolean>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const getStoredUserType = (): string | UserTypeValue[] | null => {
  const stored = localStorage.getItem("userType");
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed)) {
      return parsed as UserTypeValue[];
    }
    return parsed as string;
  } catch {
    return stored;
  }
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem("token")
  );
  const [userType, setUserType] = useState<string | UserTypeValue[] | null>(
    () => getStoredUserType()
  );

  const isAuthenticated = !!token;

  const login = async (identifier: string, password: string) => {
    try {
      // Determine if identifier is a phone (all digits, length >= 7) or username
      const isPhone = /^\d{7,}$/.test(identifier);
      const body: any = { password };
      if (isPhone) {
        body.phone = identifier;
      } else {
        body.username = identifier;
      }
      const res = await fetch(`${config.backendUrl}/users/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.message === "User not found") {
        setError("טלפון/שם משתמש או סיסמה שגויים");
        return { success: false, userType: null };
      }
      if (
        !data.userType.includes(USER_TYPES.ADMIN) &&
        !data.userType.includes(USER_TYPES.SUPER_ADMIN) &&
        !data.userType.includes(USER_TYPES.RENTAL) &&
        !data.userType.includes(USER_TYPES.INSPECTION) &&
        !data.userType.includes(USER_TYPES.OPERATE) &&
        !data.userType.includes(USER_TYPES.FREESBE)
      ) {
        setError("אין לך הרשאות ניהול פנה למנהל המערכת");
        return { success: false, userType: null };
      }
      setToken(data.token);
      setUserType(data.userType);
      localStorage.setItem("token", data.token);
      localStorage.setItem("userType", JSON.stringify(data.userType));
      return { success: true, userType: data.userType };
    } catch (e) {
      return { success: false, userType: null };
    }
  };

  const logout = () => {
    setToken(null);
    setUserType(null);
    localStorage.removeItem("token");
    localStorage.removeItem("userType");
  };

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [reloadUsers, setReloadUsers] = useState(false);

  const getUsers = async (
    page = 1,
    reset = false,
    phone?: string,
    userTypeFilter?: string
  ) => {
    if (page === 1 || reset) {
      setLoading(true);
      setCurrentPage(1);
    } else {
      setLoadingMore(true);
    }

    try {
      let url = `${config.backendUrl}/users/get-users?page=${page}&limit=100`;
      if (phone) {
        url += `&phone=${encodeURIComponent(phone)}`;
      }
      if (userTypeFilter) {
        url += `&userType=${encodeURIComponent(userTypeFilter)}`;
      }
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
      });
      const data = await res.json();

      if (data.users && Array.isArray(data.users)) {
        if (page === 1 || reset) {
          setUsers(data.users);
        } else {
          setUsers((prevUsers) => [...prevUsers, ...data.users]);
        }

        // Use the hasMore from the API response, or fallback to length check
        setHasMore(
          data.hasMore !== undefined ? data.hasMore : data.users.length === 100
        );
        setCurrentPage(page);
      } else {
        if (page === 1 || reset) {
          setUsers([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      if (page === 1 || reset) {
        setUsers([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const [error, setError] = useState<string | null>(null);
  const [usersWithActiveChat, setUsersWithActiveChat] = useState<ChatUser[]>(
    []
  );
  const [usersWithChatHistory, setUsersWithChatHistory] = useState<ChatUser[]>(
    []
  );
  const [isActiveChat, setIsActiveChat] = useState(true);

  const getUsersWithActiveChat = useCallback(async () => {
    setLoading(true);
    const response = await fetch(
      `${config.backendUrl}/users/get-all-users-with-active-chat`,
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    const data = await response.json();
    setLoading(false);
    return data.users;
  }, [config.backendUrl, token]);

  const getUsersWithChatHistory = useCallback(async () => {
    setLoading(true);
    const response = await fetch(
      `${config.backendUrl}/users/get-all-users-with-chat-history`,
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    const data = await response.json();
    setLoading(false);
    return data.users;
  }, [config.backendUrl, token]);

  const deleteMessage = useCallback(
    async (userId: string, messageId: string) => {
      try {
        const response = await fetch(
          `${config.backendUrl}/users/delete-message/${encodeURIComponent(
            userId
          )}/${encodeURIComponent(messageId)}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        if (!response.ok) return false;
        const removeFromUsers = (users: ChatUser[]) =>
          users.map((u) =>
            u._id === userId
              ? {
                  ...u,
                  messages: (u.messages || []).filter(
                    (m) => m._id !== messageId
                  ),
                }
              : u
          );
        setUsersWithActiveChat((prev) => removeFromUsers(prev));
        setUsersWithChatHistory((prev) => removeFromUsers(prev));
        return true;
      } catch (e) {
        return false;
      }
    },
    [config.backendUrl, token]
  );

  useEffect(() => {
    setToken(localStorage.getItem("token"));
    setUserType(getStoredUserType());
  }, []);

  return (
    <UserContext.Provider
      value={{
        error,
        setError,
        loading,
        setLoading,
        token,
        userType,
        setUserType,
        login,
        logout,
        isAuthenticated,
        users,
        setUsers,
        getUsers,
        hasMore,
        loadingMore,
        setLoadingMore,
        reloadUsers,
        setReloadUsers,
        usersWithActiveChat,
        setUsersWithActiveChat,
        usersWithChatHistory,
        setUsersWithChatHistory,
        isActiveChat,
        setIsActiveChat,
        getUsersWithActiveChat,
        getUsersWithChatHistory,
        deleteMessage,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be used within a UserProvider");
  return context;
};
